/**
 * Excluir uma automação de vez (ADR-0029): a confirmação.
 *
 * Diferente de arquivar, não tem volta: o serviço apaga as versões, as
 * execuções e as rodadas. Por isso a pessoa digita o nome da automação para
 * liberar o botão, e o texto diz o que some. A ativa nem chega aqui: o menu só
 * oferece "Excluir" fora do ar.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { Botao, Campo, Dialogo, Icone } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import './excluir-automacao.css';

export function ExcluirAutomacao({
  nome,
  excluindo,
  falha,
  aoConfirmar,
  aoFechar,
}: {
  nome: string;
  excluindo: boolean;
  falha: string | null;
  aoConfirmar: () => void;
  aoFechar: () => void;
}): ReactNode {
  const [digitado, definirDigitado] = useState('');
  const [copiado, definirCopiado] = useState(false);
  // O nome para copiar e colar: o que se confere é a intenção, não a digitação.
  const copiar = (): void => {
    void navigator.clipboard?.writeText(nome).then(
      () => definirCopiado(true),
      () => definirCopiado(false),
    );
  };
  const confere = digitado.trim() === nome.trim();

  const enviar = (e: FormEvent): void => {
    e.preventDefault();
    if (confere && !excluindo) aoConfirmar();
  };

  return (
    <Dialogo
      titulo="Excluir a automação?"
      largura="estreita"
      consequencia
      papel="alertdialog"
      descricaoId="excluir-automacao-aviso"
      aoFechar={aoFechar}
      botoes={
        <>
          <Botao
            variante="fantasma"
            onClick={aoFechar}
            desabilitado={excluindo}
            motivo="Excluindo…"
          >
            Cancelar
          </Botao>
          <Botao
            type="submit"
            form="excluir-automacao"
            variante="perigo"
            desabilitado={!confere || excluindo}
            motivo={excluindo ? 'Excluindo…' : 'Digite o nome da automação para confirmar.'}
          >
            {excluindo ? 'Excluindo…' : 'Excluir de vez'}
          </Botao>
        </>
      }
    >
      <form id="excluir-automacao" className="excluir-automacao" onSubmit={enviar} noValidate>
        <p id="excluir-automacao-aviso">
          <strong>{nome}</strong> será apagada de vez, com as versões e o histórico de execuções.
          Não dá para desfazer. Se for só para tirar da lista, use “Arquivar”.
        </p>
        <div className="excluir-automacao__nome">
          <code className="excluir-automacao__texto">{nome}</code>
          <Botao variante="secundario" miudo onClick={copiar} aria-label={`Copiar o nome ${nome}`}>
            <Icone nome={copiado ? 'check' : 'copiar'} />
            {copiado ? 'Copiado' : 'Copiar'}
          </Botao>
        </div>
        <p className="visualmente-oculto" role="status">
          {copiado ? 'Nome copiado.' : ''}
        </p>
        <Campo
          rotulo="Digite ou cole o nome da automação para confirmar"
          autoComplete="off"
          value={digitado}
          onChange={(e) => definirDigitado(e.target.value)}
        />
        {falha && <Faixa tom="bloqueado">{falha}</Faixa>}
      </form>
    </Dialogo>
  );
}
